import { server as activeServer } from './server.active';
import ActiveError from './error';
import ServerCookie from './server.cookie';
import Cookie from './server.virtualCookie';
import * as querystring from 'querystring';
import { createInfo, Info, ExtendedObject } from './info';

type Method = 'OPTIONS' | 'GET' | 'HEAD' | 'POST' | 'PUT' | 'DELETE' | 'TRACE' | 'CONNECT';
export interface VirtualRequest {
	domain: string;
	path: string;
	query: any;
	method: Method;
	isVirtualRequest: true;
	body: any;
	headers: {[key: string]: string | string[]};
	cookie?: Cookie | string;
}

export interface VirtualResponse {
	statusCode: number;
	headers: { [key: string]: string | string[] };
	cookies: Cookie;
	data?: any;
	transfer?: string;
}

function setHeader(headers: { [key: string]: string | string[] }, name: string, value: string | string[]) {
	headers[name.toLowerCase()] = value;
}
interface baseRequest {
	(server: activeServer, request: VirtualRequest): Promise<VirtualResponse>;
}
/** 通过 activeServer 发起虚拟请求 */
async function baseRequest(server: activeServer, request: VirtualRequest): Promise<VirtualResponse> {
	const serverCookie = new ServerCookie(request.cookie ? String(request.cookie) : '');
	const headers: { [key: string]: string | string[] } = {};
	const response = await server(request.domain + request.path, request.query, request, {
		method: request.method, domain: request.domain, path: request.path,
		getCookie: (...p) => serverCookie.get(...p),
		setCookie: (...p) => serverCookie.set(...p),
		clearCookie: (...p) => serverCookie.clear(...p),
		getHeader(name: string) { return request.headers[name] },
		setHeader(name: string, value: string | string[]): void { setHeader(headers, name, value) },
		clientIp: ['127.0.0.1'],
		isVirtual: true,
	});
	const cookie = serverCookie.header;
	headers.cookie = cookie;
	const cookies = new Cookie(cookie);
	if (!response) {
		return { statusCode: 404, headers, cookies, }
	} else {
		const data = response.error || response._value;
		const {transfer, statusCode } = response;
		if (transfer) {
			headers.location = transfer;
		}
		return { statusCode, headers, cookies, data, }
	}
}


export interface queryBaseOpt {
	[key: string]: string | number | (string | number)[]
};
export type queryOpt = queryBaseOpt | string;

export interface netRequest {
	url?: string;
	domain?: string;
	path?: string;
	method?: Method;
	header?: { [key: string]: string | string[] };
	headers?: { [key: string]: string | string[] };
	cookie?: Cookie | string | true;
	query?: queryOpt;
	body?: any;
	data?: any;
	form?: any;
	type?: any;
	onlyData?: boolean;
	responseType?: string;
};

function getBaseParam<T>(param: string | netRequest, body?: T): netRequest {
	if (typeof param !== 'string') { return param; }
	const netRequest: netRequest = /^https?:\/\//i.test(param) ? {url: param} : {path: param};
	netRequest.onlyData = true;
	netRequest.body = body;
	return netRequest;
}
/** 处理请求参数 */
function getParam<T>(param: string | netRequest, paramBody?: T): VirtualRequest & {responseType?: string, onlyData?: boolean} {
	let {
		url, domain, path, method,
		header, headers, cookie,
		query, body, data, form,
		onlyData = false, responseType
	} = getBaseParam(param, paramBody);
	if (!method) { method = 'GET' };
	if (!path) {
		url = url || '';
		let urldomain;
		[, urldomain, path] = /^(?:https?:)?((?:\/\/(?:[a-z0-9\-]+\.)*(?:[a-z0-9]+)(?:\:\d+)?)?)(\/.*)/i.exec(url) || ['','', url];
		if (urldomain) {
			[, domain] = /^(?:\/\/((?:[a-z0-9\-]+\.)*(?:[a-z]+))(?:\:\d+)?)$/.exec(urldomain) || ['', 'localhost'];
		}
	}
	if (!domain) {
		domain = 'localhost';
	}
	if (typeof path !== 'string') {
		path = '/';
	} else if (path[0] !== '/') {
		path = '/' + path;
	}
	if (typeof query === 'string') {
		query = querystring.parse(query);
	} else if (!query) {
		query = {};
	}
	let search: string;
	[, path, search] = /^([^?#]*)((?:\?[^#]*)?)((?:#.*)?)$/.exec(path) as RegExpExecArray;
	if (search) {
		const searchs = querystring.parse(search);
		for (let k in searchs) {
			query[k] = searchs[k];
		}
	}

	headers = headers || header;
	headers = typeof headers === 'object' && headers || {};

	headers.host = headers.host || (domain || 'localhost');
	headers.origin = headers.origin ||  'https://' + headers.host;
	if (form) {
		body = form;
	} else if (!body) {
		body = data;
	}

	if (!body) {
		body = {};
	} else if ((method == 'GET' || method === 'HEAD')) {
		for (let k in body) {
			query[k] = body[k];
		}
		body = {};
	}
	return {domain, path, method, query, headers, cookie: typeof cookie === 'string' || cookie instanceof Cookie ? cookie : '', body: body, onlyData, isVirtualRequest: true, responseType};
}


interface request {
	(param: netRequest & { onlyData: true }): Promise<any>;
	(param: netRequest & { onlyData?: false }): Promise<VirtualResponse>;
	(param: string, data?: object): Promise<any>;
}
function packRequest(baseRequest: baseRequest, server: activeServer): request {
	function request(param: netRequest & { onlyData: true }): Promise<any>;
	function request(param: netRequest & { onlyData?: false }): Promise<VirtualResponse>;
	function request(param: string, data?: object): Promise<any>;
	function request(param: string | netRequest, data?: object) {
		const { responseType, onlyData, ...request } = getParam(param, data);
		const result = baseRequest(server, request);
		const ret = result.then(({statusCode, headers, data, cookies}) => {
			let transfer = '';
			if (statusCode === 301 || statusCode === 302) {
				transfer = headers['location'] as string;
			}else if (statusCode >= 400) {
				throw new ActiveError(':net:' + statusCode);
			}
		
			if (responseType === 'json+error' && data instanceof ActiveError) {
				throw data;
			}
		
			return onlyData ? data : { data, headers, statusCode, transfer, cookies  };
		});
		return ret;
	}
	return request;
}

export interface BaseRequest {
	/** 发起 HTTP 请求 */
	request: request;
}

function packRequestMethodWithData(request: request, method: Method) {
	async function post(url: string, data?: any, query?: queryBaseOpt): Promise<any>;
	async function post(path: string, domain: string, data?: any, query?: queryBaseOpt): Promise<any>;
	async function post(path: string, domain: string | any, data: any | queryBaseOpt, query?: queryBaseOpt): Promise<any> {
		const opt: netRequest = { method, onlyData: true };
		if (path[0] === '/' && domain && typeof domain === 'string') {
			opt.path = path;
			opt.domain = domain;
		} else {
			opt.url = path;
			[data, query] = [domain, data];
		}
		opt.data = data;
		opt.query = query;
		return request(opt as netRequest & { onlyData: true });
	}
	return post;
}

function packRequestMethodWithoutData(request: request, method: Method) {
	async function get(url: string, query?: queryBaseOpt): Promise<any>;
	async function get(path: string, domain: string, query?: queryBaseOpt): Promise<any>;
	async function get(path: string, domain?: string | queryBaseOpt, query?: queryBaseOpt): Promise<any> {
		const opt: netRequest = { method, onlyData: true };
		if (path[0] === '/' && domain && typeof domain === 'string') {
			opt.path = path;
			opt.domain = domain;
		} else {
			opt.url = path;
			query = domain as queryBaseOpt;
		}
		opt.query = query;
		return request(opt as netRequest & { onlyData: true });
	}
	return get;
}

export interface MethodRequest {
	/**
	 * 发起 GET 请求
	 * @param url 请求Url
	 * @param query Url查询字符串
	 */
	get<Q extends object, R = any>(url: string, query?: Q): Promise<R>;
	/**
	 * 发起 GET 请求
	 * @param path 请求路径
	 * @param domain 请求域名
	 * @param query Url查询字符串
	 */
	get<Q extends object, R = any>(path: string, domain: string, query?: Q): Promise<R>;
	/**
	 * 发起 POST 请求
	 * @param url 请求Url
	 * @param data 请求数据
	 * @param query Url查询字符串
	 */
	post<Q extends object, B extends object, R = any>(url: string, body?: B, query?: Q): Promise<R>;
	/**
	 * 发起 POST 请求
	 * @param path 请求路径
	 * @param domain 请求域名
	 * @param data 请求数据
	 * @param query Url查询字符串
	 */
	post<Q extends object, B extends object, R = any>(path: string, domain: string, body?: B, query?: Q): Promise<R>;
	/**
	 * 发起 PUT 请求
	 * @param url 请求Url
	 * @param data 请求数据
	 * @param query Url查询字符串
	 */
	put<Q extends object, B extends object, R = any>(url: string, body?: B, query?: Q): Promise<R>;
	/**
	 * 发起 PUT 请求
	 * @param path 请求路径
	 * @param domain 请求域名
	 * @param data 请求数据
	 * @param query Url查询字符串
	 */
	put<Q extends object, B extends object, R = any>(path: string, domain: string, body?: B, query?: Q): Promise<R>;
	/**
	 * 发起 DELETE 请求
	 * @param url 请求Url
	 * @param data 请求数据
	 * @param query Url查询字符串
	 */
	delete<Q extends object, B extends object, R = any>(url: string, body?: B, query?: Q): Promise<R>;
	/**
	 * 发起 DELETE 请求
	 * @param path 请求路径
	 * @param domain 请求域名
	 * @param data 请求数据
	 * @param query Url查询字符串
	 */
	delete<Q extends object, B extends object, R = any>(path: string, domain: string, body?: B, query?: Q): Promise<R>;
}
function packRequestMethod(request): MethodRequest {
	return {
		get: packRequestMethodWithoutData(request, 'GET'),
		post: packRequestMethodWithData(request, 'POST'),
		put: packRequestMethodWithData(request, 'PUT'),
		delete: packRequestMethodWithData(request, 'DELETE'),
	}
}

function bindFunc(request: request, baseParam: netRequest & {query?: queryBaseOpt}, cookie: Cookie | null) {
	function bind(param: netRequest & { onlyData: true }): Promise<any>;
	function bind(param: netRequest): Promise<VirtualResponse>;
	function bind(param: string, data?: object): Promise<any>;
	function bind(param: string | netRequest, data?: object) {
		const newParam = getBaseParam(param, data);
		if (newParam.query) {
			if (newParam.query && typeof newParam.query === 'object') {
				const baseQuery = newParam.query;
				const query = newParam.query;
				for (let k in baseQuery) {
					if (k in query) { continue; }
					query[k] = baseQuery[k];
				}
			} else {
				newParam.query = newParam.query;
			}
		}
		if ('domain' in baseParam && !('domain' in newParam)) { newParam.domain = baseParam.domain; }
		if ('method' in baseParam && !('method' in newParam)) { newParam.method = baseParam.method; }
		if ('responseType' in baseParam && !('responseType' in newParam)) { newParam.responseType = baseParam.responseType; }
	
		let onlyData = typeof newParam.onlyData === 'boolean' ? newParam.onlyData : baseParam.onlyData;
		newParam.onlyData = false;
		let useGlobalCookie = false;
		if (cookie && !('cookie' in newParam)) {
			useGlobalCookie = true;
			newParam.cookie = cookie;
		}
		const result = request(newParam as netRequest & {onlyData: false});
		const ret = result.then(ret =>  {
			if (useGlobalCookie) { (cookie as Cookie).merge(ret.cookies); }
			return onlyData ? ret.data : ret;
		});
		return ret;
	}
	return bind;
}

export interface BindRequest extends BaseRequest, MethodRequest {
	/** 根据 用户ID 登陆某用户 */
	login(uid: string): Promise<boolean | undefined>;
	/** 退出已登录的用户 */
	exit(): Promise<void>;
	/**
	 * 获取Cookie
	 * @param  {String} cname Cookie名称
	 * @return {String}       Cookie值
	 */
	getCookie(cname: string): Promise<string | undefined>;
	/**
	 * 设置Cookie
	 * @param {String} cname  Cookie名称
	 * @param {String} cookie Cookie值
	 * @param {Object} opt    Cookie选项
	 */
	setCookie(cname: string, cookie: string, opt?: object): Promise<boolean | undefined>;
	/**
	 * 清除Cookie
	 * @param {String} cname  Cookie名称
	 * @param {Object} opt    Cookie选项
	 */
	clearCookie(cname: string, opt?: object): Promise<void>;
	/**
	 * 清除所有 Cookie
	 * @param {Object} opt    Cookie选项
	 */
	clearCookie(opt?: object): Promise<void>;
	/**
	 * 获取模块的Url
	 * @param  {String} id 模块Id
	 * @return {String}    模块Id
	 */
	getUrl(id: string): Promise<string | undefined>;
	/** 获取用户ID */
	getUserId(): Promise<any>;
	/** 获取用户权限 */
	getPower(v?: string): Promise<any | { [key: string]: any } | undefined>;
	/**
	 * 判断用户是否有权限
	 * 特别的，如果用户有超级权限，则视为用户有任意权限
	 * @param  {...String} powers 要判断的权限
	 * @return {Boolean}          用户是否有列出的权限之一
	 */
	hasPower(...powers: (string | [string] | [string, (string | string[])])[]): Promise<boolean | undefined>;
}


async function execVirtuaInfo<RET, Ext extends ExtendedObject = {user: object}, UserApi extends object = {}>(
	getInfo: createInfo<Ext>,
	cookie: Cookie | null,
	domain: string = 'localhost',
	exec: (info: Info<Ext>) => RET | Promise<RET>,
): Promise<RET | undefined> {
	if (!cookie) { return; }
	const serverCookie = new ServerCookie(String(cookie));
	const info = await getInfo({
		method: 'GET', domain, path: '/',
		getCookie: (...p) => serverCookie.get(...p),
		setCookie: (...p) => serverCookie.set(...p),
		clearCookie: (...p) => serverCookie.clear(...p),
		getHeader() { return '' },
		setHeader(): void { },
		clientIp: ['127.0.0.1'],
	}, {});
	const ret = await exec(info);
	cookie.add(serverCookie.header);
	return ret;
}

export interface bind {
	(param?: (netRequest & { key?: string })): BindRequest;
}
/** 绑定请求参数 */
export function bind<Ext extends ExtendedObject = {user: object}>(
	request: request,
	getInfo: createInfo<Ext>,
	param?: netRequest
): BindRequest {
	if (!param) { param = {}; }
	if (typeof param.query !== 'object') { delete param.query; }
	const cookie = param.cookie === true ? new Cookie() : new Cookie(param.cookie) || null;
	const bindRequest = bindFunc(request, param as netRequest & {query?: queryBaseOpt}, cookie);
	const exec = execVirtuaInfo.bind(null, getInfo, cookie, param.domain) as
		<RET>(exec: (info: Info<Ext>) => RET | Promise<RET>) => Promise<RET | undefined>;
	return {
		request: bindRequest,
		...packRequestMethod(bindRequest),
		login(uid: string) { return exec(info => info.login(uid)); },
		exit() { return exec(info => info.exit()); },
		getCookie(cname: string) { return exec(info => info.getCookie(cname)); },
		setCookie(cname: string, value: string, opt?: object) { return exec(info => info.setCookie(cname, value, opt)); },
		clearCookie(cname?: string | object, opt?: object) { return exec(info => info.clearCookie(cname as string, opt)); },
		getUrl(id: string) { return exec(info => info.getUrl(id)); },
		getUserId() { return exec(info => info.userId); },
		getPower(v?: string) { return exec(info => info.getPower(v)); },
		hasPower(...powers: (string | [string] | [string, (string | string[])])[]) { return exec(info => info.hasPower(...powers)); },
	};
}

export interface server extends BaseRequest, MethodRequest {
	bind(param?: (netRequest & { key?: string })): BindRequest;
}

export default function create<Ext extends ExtendedObject = {user: object}>(
	activeServer: activeServer,
	getInfo: createInfo<Ext>): server {
	const request = packRequest(baseRequest, activeServer);
	return {
		request,
		...packRequestMethod(request),
		bind: (param?) => bind(request, getInfo, param),
	};
}
